import PropTypes from 'prop-types';
import { forwardRef, useImperativeHandle, useMemo, useRef, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import clsx from 'clsx';
import { getExtensionComponents } from '@icp/settings';
import { selectContext } from '@icp/form-renderer-core';
import { useSelector } from '../../store/StoreProvider';
import { useClassName } from '../../hooks';
import { withFieldWrapper } from '../../fieldWrapper';
import { useElementDecorator, useIsInDesign } from '../../FormRenderCtx';
import ImportCss from './ImportCss';
import RemoteCommon from './RemoteCommon';
import useExtensionParams from './useExtensionParams';

const ExtensionWrapper = forwardRef(function ExtensionWrapper(props, ref) {
  const {
    keyPath,
    className: classNameProp,
    style: styleProp,
    componentProps = {},
    fields,
  } = props;

  const {
    className: componentClassName,
    style: componentStyle,
    extensionId,
    // 兼容老项目只配了extensionId
    extensionType = extensionId ? 'local-react' : undefined,
    extensionParams,
    extensionJsUrl,
    extensionCssUrl,
    extensionJs,
    extensionCss,
    suppressShadow: suppressShadowProp,
  } = componentProps;
  const actAsField = fields?.[0];

  const isInDesign = useIsInDesign();
  const projectToken = useSelector(selectContext)?.projectToken;
  const pbcToken = useSelector(selectContext)?.pbcToken;

  // 本地扩展不使用shadow方便css引用和本地打包
  const suppressShadow = suppressShadowProp || !!extensionId;

  const ElementDecorator = useElementDecorator();

  const className = clsx(useClassName(classNameProp), useClassName(componentClassName));
  const style = { ...styleProp, ...componentStyle };

  const shadowHostRef = useRef(null);

  useImperativeHandle(
    ref,
    () => ({
      shadowHost: shadowHostRef.current,
    }),
    [],
  );

  const [shadowRoot, setShadowRoot] = useState(null);

  useEffect(() => {
    if (!suppressShadow) {
      setShadowRoot(shadowHostRef.current.attachShadow({ mode: 'open' }));
    } else {
      setShadowRoot(null);
    }
  }, [suppressShadow]);

  const extParams = useExtensionParams(extensionParams);

  const portal = useMemo(() => {
    if (isInDesign && extensionType === 'local-react') {
      return (
        <pre>
          <code>
            {JSON.stringify(
              {
                extensionId,
                extensionType,
                extensionParams: extParams,
                extensionJsUrl,
                extensionCssUrl,
                extensionJs,
                extensionCss,
              },
              null,
              2,
            )}
          </code>
        </pre>
      );
    }
    if (extensionType === 'local-react' && extensionId) {
      const Comp = getExtensionComponents()?.[extensionId];
      return (
        <ImportCss url={extensionCssUrl}>
          <Comp {...extParams} />
        </ImportCss>
      );
    }
    if (extensionType === 'remote-common' && extensionJsUrl) {
      return (
        <ImportCss url={extensionCssUrl}>
          <RemoteCommon
            jsUrl={extensionJsUrl}
            mountParams={extParams}
            shadowHostRef={shadowHostRef}
            actAsField={actAsField}
          />
        </ImportCss>
      );
    }
    if (extensionType === 'remote-common-ceta-hosted' && extensionJs?.filename) {
      const urlPrefix = `/form/api/ui-plugin${isInDesign ? `/${projectToken}` : ''}/ui-components/${pbcToken}/`;
      const jsUrl = `${urlPrefix}${extensionJs?.filename}`;
      const cssUrl = extensionCss?.filename ? `${urlPrefix}${extensionCss?.filename}` : null;
      // 后端不支持从cookie读access token，所以这里需要前端手动加载文件，导致无法使用浏览器缓存。后端支持cookie鉴权后，manualLoaded改成false即可。
      return (
        <ImportCss url={cssUrl} manualLoad={true}>
          <RemoteCommon
            jsUrl={jsUrl}
            mountParams={extParams}
            shadowHostRef={shadowHostRef}
            manualLoad={true}
            actAsField={actAsField}
          />
        </ImportCss>
      );
    }
    return null;
  }, [
    isInDesign,
    extensionType,
    extensionId,
    extensionJsUrl,
    extensionCssUrl,
    extensionJs,
    extensionCss,
    extParams,
    pbcToken,
    projectToken,
    actAsField,
  ]);

  return (
    <ElementDecorator keyPath={keyPath}>
      <div
        key={`suppress-shadow-${suppressShadow}`}
        className={clsx(
          'extension-wrapper form-wrapper',
          isInDesign && 'design-mode no-children',
          isInDesign,
          className,
        )}
        style={style}
        ref={shadowHostRef}
      >
        {suppressShadow ? portal : null}
        {!suppressShadow && portal && shadowRoot ? createPortal(portal, shadowRoot) : null}
      </div>
    </ElementDecorator>
  );
});

ExtensionWrapper.propTypes = {
  keyPath: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.number, PropTypes.string])),
  className: PropTypes.string,
  style: PropTypes.shape({}),
  componentProps: PropTypes.shape({
    className: PropTypes.string,
    style: PropTypes.shape({}),
    extensionType: PropTypes.oneOf(['local-react', 'remote-common', 'remote-common-ceta-hosted']),
    extensionId: PropTypes.string,
    extensionParams: PropTypes.shape({}),
    extensionJsUrl: PropTypes.string,
    extensionCssUrl: PropTypes.string,
  }),
  fields: PropTypes.arrayOf(PropTypes.shape({})),
};

// for @icp/utils/getComponentDisplayName, otherwise, in production mode, function name will be compressed.
ExtensionWrapper.displayName = 'Extension';

export default withFieldWrapper(ExtensionWrapper);
